import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ExternalLink, Lock } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { documentsService } from "@/services/documentsService";
import { formatRelativeTime } from "@/lib/format";
import { SimpleListPage } from "@/modules/_shared/SimpleListPage";
import type { Column } from "@/components/organisms/DataTable";
import type { DocumentFilter, DocumentSummary } from "@/types/entities";

const columns: Column<DocumentSummary>[] = [
  {
    id: "name",
    header: "Document",
    sortable: true,
    cell: (r) => (
      <div className="min-w-0 flex items-center gap-2">
        {r.isPrivate && <Lock size={12} className="text-bridge-muted" />}
        <div className="min-w-0">
          <div className="truncate font-medium text-bridge-fg">{r.name ?? "(no name)"}</div>
          <div className="truncate text-xs text-bridge-muted">{r.caption ?? r.fileType ?? ""}</div>
        </div>
      </div>
    ),
  },
  { id: "fileType", header: "Type", sortable: true, cell: (r) => r.fileType ?? "—" },
  { id: "associatedType", header: "Linked", sortable: true, cell: (r) => r.associatedType ?? "—" },
  {
    id: "isPrivate",
    header: "Visibility",
    cell: (r) =>
      r.isPrivate ? <Badge tone="warning">Private</Badge>
      : <Badge tone="neutral">Public</Badge>,
  },
  {
    id: "url",
    header: "Open",
    cell: (r) =>
      r.url ? (
        <a
          href={r.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-bridge-accentSoft hover:text-bridge-accent"
          onClick={(e) => e.stopPropagation()}
        >
          Open <ExternalLink size={12} />
        </a>
      ) : <span className="text-bridge-subtle">—</span>,
  },
  {
    id: "atlasModifiedAt",
    header: "Updated",
    sortable: true,
    align: "right",
    cell: (r) => <span className="text-bridge-muted">{formatRelativeTime(r.atlasModifiedAt)}</span>,
  },
];

export function DocumentsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const externalPropertyId = searchParams.get("externalPropertyId") || undefined;
  const returnView = searchParams.get("returnView") === "tile" ? "tile" : "list";
  const isPropertyContext = Boolean(externalPropertyId);

  const handleBackToProperties = useCallback(() => {
    navigate(`/properties?view=${returnView}`);
  }, [navigate, returnView]);

  return (
    <SimpleListPage<DocumentSummary, DocumentFilter>
      title="Documents"
      subtitle="Property documents, leases, certificates."
      fetcher={documentsService.list}
      columns={columns}
      rowKey={(r) => r.id}
      searchPlaceholder="Search by name, caption, file type..."
      emptyTitle="No documents match"
      initialFilter={externalPropertyId ? { externalPropertyId } : undefined}
      onBack={isPropertyContext ? handleBackToProperties : undefined}
      backAriaLabel="Back to properties"
      showPageLoader
    />
  );
}